import React from "react";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import "./AdminStoreProducts.css";

const DEFAULT_FORM = {
  categoryId: "",
  title: "",
  description: "",
  price: "",
  points: "",
  promotionalPrice: "",
  showInPromotions: true,
  showInHighlights: true,
};

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const formatCurrency = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return currencyFormatter.format(number);
};

const AdminStoreProducts = () => {
  const [menuClose, setMenuClose] = React.useState(false);
  const [categories, setCategories] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const fileInputRef = React.useRef(null);

  const toggleMenu = () => setMenuClose((prev) => !prev);

  const loadData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [categoriesResponse, productsResponse] = await Promise.all([
        Api.get("/admin/store-categories"),
        Api.get("/admin/store-products"),
      ]);

      setCategories(Array.isArray(categoriesResponse?.data?.items) ? categoriesResponse.data.items : []);
      setProducts(Array.isArray(productsResponse?.data?.items) ? productsResponse.data.items : []);
    } catch (err) {
      console.error("Erro ao carregar dados da store:", err);
      setError("Nao foi possivel carregar categorias e produtos da Store.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;

    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setError("");
    setSuccess("");
  };

  const handleSelectFiles = (event) => {
    setSelectedFiles(Array.from(event.target.files || []));
    setError("");
    setSuccess("");
  };

  const resetForm = () => {
    setForm(DEFAULT_FORM);
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const formData = new FormData();
    formData.append("categoryId", form.categoryId);
    formData.append("title", form.title);
    formData.append("description", form.description);
    formData.append("price", form.price);
    formData.append("points", form.points);
    formData.append("promotionalPrice", form.promotionalPrice);
    formData.append("showInPromotions", String(form.showInPromotions));
    formData.append("showInHighlights", String(form.showInHighlights));
    selectedFiles.forEach((file) => formData.append("images", file));

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const response = await Api.post("/admin/store-products", formData, {
        timeout: 30000,
      });

      const created = response?.data?.item;
      if (created) {
        setProducts((current) => [created, ...current]);
      }

      resetForm();
      setSuccess("Produto criado com sucesso.");
    } catch (err) {
      console.error("Erro ao criar produto da store:", err);
      setError(err?.response?.data?.message || "Nao foi possivel criar o produto.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Deseja remover este produto da Store?")) return;

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");
      await Api.delete(`/admin/store-products/${productId}`);
      setProducts((current) => current.filter((item) => item.id !== productId));
      setSuccess("Produto removido com sucesso.");
    } catch (err) {
      console.error("Erro ao remover produto da store:", err);
      setError(err?.response?.data?.message || "Nao foi possivel remover o produto.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-container">
      <SidebarAdmin close={menuClose} storeProductsActive="active" />

      <main className="main-content">
        <Topbar page="Store / Produtos" onToggleMenu={toggleMenu} />

        <section className="admin-store-products-grid">
          <article className="admin-store-products-panel">
            <div className="admin-store-products-header">
              <div>
                <h2>Cadastrar produto</h2>
                <p>
                  Adicione titulo, descricao, valor, pontos e varias fotos. Se houver mais de uma
                  foto, o produto aparece com carrossel na Store.
                </p>
              </div>
              <span className="admin-store-products-badge">Super administrador</span>
            </div>

            <form className="admin-store-products-form" onSubmit={handleSubmit}>
              <label className="admin-store-products-field">
                <span>Categoria</span>
                <select value={form.categoryId} onChange={handleChange("categoryId")}>
                  <option value="">Selecione</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="admin-store-products-field admin-store-products-field-wide">
                <span>Titulo</span>
                <input
                  value={form.title}
                  onChange={handleChange("title")}
                  placeholder="Ex.: Camisa Polo Premium"
                />
              </label>

              <label className="admin-store-products-field admin-store-products-field-full">
                <span>Descricao</span>
                <textarea
                  value={form.description}
                  onChange={handleChange("description")}
                  placeholder="Descreva o produto"
                  rows={4}
                />
              </label>

              <label className="admin-store-products-field">
                <span>Valor</span>
                <input value={form.price} onChange={handleChange("price")} placeholder="79.90" />
              </label>

              <label className="admin-store-products-field">
                <span>Pontos</span>
                <input value={form.points} onChange={handleChange("points")} placeholder="1200" />
              </label>

              <label className="admin-store-products-field">
                <span>Valor promocional</span>
                <input
                  value={form.promotionalPrice}
                  onChange={handleChange("promotionalPrice")}
                  placeholder="59.90"
                />
              </label>

              <div className="admin-store-products-checkboxes admin-store-products-field-full">
                <label className="admin-store-products-checkbox">
                  <input
                    type="checkbox"
                    checked={form.showInPromotions}
                    onChange={handleChange("showInPromotions")}
                  />
                  <span>Mostrar em promocoes</span>
                </label>

                <label className="admin-store-products-checkbox">
                  <input
                    type="checkbox"
                    checked={form.showInHighlights}
                    onChange={handleChange("showInHighlights")}
                  />
                  <span>Mostrar em destaque</span>
                </label>
              </div>

              <label className="admin-store-products-upload-box admin-store-products-field-full" htmlFor="store-product-images">
                <span>Selecionar fotos do produto</span>
                <small>Envie uma ou mais imagens. A ordem do envio sera a ordem do carrossel.</small>
              </label>

              <input
                ref={fileInputRef}
                id="store-product-images"
                type="file"
                accept="image/*"
                multiple
                onChange={handleSelectFiles}
              />

              {selectedFiles.length > 0 ? (
                <div className="admin-store-products-file-list admin-store-products-field-full">
                  {selectedFiles.map((file) => (
                    <div className="admin-store-products-file-item" key={`${file.name}-${file.lastModified}`}>
                      <strong>{file.name}</strong>
                      <span>{Math.max(1, Math.round(file.size / 1024))} KB</span>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="admin-store-products-actions admin-store-products-field-full">
                <button type="submit" disabled={submitting}>
                  {submitting ? "Salvando..." : "Criar produto"}
                </button>
              </div>

              {error ? <div className="admin-store-products-message error">{error}</div> : null}
              {success ? <div className="admin-store-products-message success">{success}</div> : null}
            </form>
          </article>

          <article className="admin-store-products-panel">
            <div className="admin-store-products-header compact">
              <div>
                <h2>Produtos cadastrados</h2>
                <p>{loading ? "Carregando..." : `${products.length} produto(s) ativos`}</p>
              </div>
            </div>

            {loading ? (
              <div className="admin-store-products-empty">Carregando produtos...</div>
            ) : products.length === 0 ? (
              <div className="admin-store-products-empty">Nenhum produto cadastrado ainda.</div>
            ) : (
              <div className="admin-store-products-list">
                {products.map((product) => (
                  <article className="admin-store-product-card" key={product.id}>
                    <div className="admin-store-product-preview">
                      {product.images?.[0]?.url ? (
                        <img src={product.images[0].url} alt={product.title} />
                      ) : (
                        <div className="admin-store-product-placeholder">Sem imagem</div>
                      )}
                    </div>

                    <div className="admin-store-product-body">
                      <strong>{product.title}</strong>
                      <span>{product.categoryName}</span>
                      {product.description ? <p>{product.description}</p> : null}

                      <div className="admin-store-product-meta">
                        {product.price !== null ? <span>Valor: {formatCurrency(product.price)}</span> : null}
                        {product.promotionalPrice !== null ? (
                          <span>Promocao: {formatCurrency(product.promotionalPrice)}</span>
                        ) : null}
                        {product.points !== null ? <span>Pontos: {product.points}</span> : null}
                        {product.showInPromotions ? <span>Em promocoes</span> : null}
                        {product.showInHighlights ? <span>Em destaque</span> : null}
                        <span>{product.images?.length || 0} foto(s)</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="admin-store-product-remove"
                      onClick={() => handleDelete(product.id)}
                      disabled={submitting}
                    >
                      Remover
                    </button>
                  </article>
                ))}
              </div>
            )}
          </article>
        </section>
      </main>
    </div>
  );
};

export default AdminStoreProducts;
